

import { getPricingPlan } from '@common/locales';
import {
  YEARLY_PRICE_MULTIPLIER,
  type BillingInterval,
} from '@common/schemas/subscription-plan.schema';
import { Check, AutoAwesome as Sparkles } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SubscriptionPlanChangePreview } from '@/backend/contracts/billing';

import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { SubscriptionFeature, SubscriptionPlan } from '@/types/subscription';

interface PlanCardProps {
  plan: SubscriptionPlan;
  features: SubscriptionFeature[];
  billingInterval: BillingInterval;
  isCurrentPlan?: boolean;
  isRecommended?: boolean;
  currentPlanPrice?: number;
  hasExistingSubscription?: boolean;
  billingFlowLocked?: boolean;
  onBillingFlowStart?: () => void;
  onBillingFlowEnd?: () => void;
}

function getFunctionErrorReason(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;

  const details = (error as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) return null;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : null;
}

function isExistingSubscriptionError(error: unknown): boolean {
  return getFunctionErrorReason(error) === 'existing-subscription';
}

function formatMoney(cents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatZeroPrice(currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);
  } catch {
    return `0 ${currency.toUpperCase()}`;
  }
}

function formatEffectiveAt(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(date);
}

export function PlanCard({
  plan,
  billingInterval,
  isCurrentPlan,
  isRecommended,
  currentPlanPrice = 0,
  hasExistingSubscription = false,
  billingFlowLocked = false,
  onBillingFlowStart,
  onBillingFlowEnd,
}: PlanCardProps) {
  const { t, i18n } = useTranslation();

  const { backend, user: authUser } = useFirebase();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [changePreview, setChangePreview] = useState<SubscriptionPlanChangePreview | null>(null);

  const locale = i18n.resolvedLanguage || i18n.language || 'en';

  const loadPlanChangePreview = async () => {
    if (!backend) return;
    const preview = await backend.billing.previewPlanChange({
      planId: plan.id,
      billingInterval,
    });
    setChangePreview(preview);
  };

  const handleSelectPlan = async () => {
    if (isCurrentPlan || billingFlowLocked || !backend || !authUser) return;

    let handlingPlanChange = hasExistingSubscription;
    onBillingFlowStart?.();
    setLoading(true);
    try {
      if (hasExistingSubscription) {
        await loadPlanChangePreview();
      } else {
        try {
          const checkoutUrl = await backend.billing.checkoutSession({
            planId: plan.id,
            billingInterval,
            successUrl: window.location.origin + '/checkout-success?session_id={CHECKOUT_SESSION_ID}',
            cancelUrl: window.location.origin + '/checkout-cancel',
          });
          window.location.href = checkoutUrl;
        } catch (checkoutError) {
          if (!isExistingSubscriptionError(checkoutError)) {
            throw checkoutError;
          }
          handlingPlanChange = true;
          await loadPlanChangePreview();
        }
      }
    } catch (err) {
      console.error('Error starting billing flow:', err);
      const reason = getFunctionErrorReason(err);
      const planChangeErrorDescription =
        reason === 'unsupported-upgrade-payment-method'
          ? t('pricing.planChange.unsupportedPaymentMethodDesc')
          : reason === 'pending-plan-change-must-be-cleared' ||
              reason === 'subscription-canceling'
            ? t('pricing.planChange.clearPendingChangeDesc')
            : t('pricing.planChange.previewErrorDesc');
      toast({
        title: handlingPlanChange
          ? t('pricing.planChange.previewErrorTitle')
          : t('pricing.subscriptionError'),
        description: handlingPlanChange
          ? planChangeErrorDescription
          : t('pricing.subscriptionErrorDesc'),
        variant: 'destructive',
      });
      onBillingFlowEnd?.();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!backend || !changePreview || confirming) return;

    const confirmedPreview = changePreview;
    setConfirming(true);
    try {
      const result = await backend.billing.confirmPlanChange({
        changeId: confirmedPreview.changeId,
      });

      if (result.outcome === 'payment_required') {
        if (!result.paymentUrl) {
          throw new Error('Plan change requires payment but no payment URL was returned');
        }
        window.location.href = result.paymentUrl;
        return;
      }

      setChangePreview(null);
      onBillingFlowEnd?.();
      const effectiveAt = formatEffectiveAt(
        result.effectiveAt ?? confirmedPreview.effectiveAt,
        locale
      );
      const description = result.outcome === 'scheduled'
        ? t('pricing.planChange.scheduledSuccessDesc', {
          planName: result.targetPlanName,
          effectiveAt,
        })
        : result.outcome === 'noop'
          ? t('pricing.planChange.noopSuccessDesc', {
            planName: result.targetPlanName,
          })
          : t('pricing.planChange.changedSuccessDesc', {
            planName: result.targetPlanName,
          });

      toast({
        title: t('pricing.planChange.successTitle'),
        description,
      });
    } catch (err) {
      console.error('Error confirming subscription plan change:', err);
      toast({
        title: t('pricing.planChange.confirmErrorTitle'),
        description: t('pricing.planChange.confirmErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleClosePlanChange = () => {
    if (confirming) return;
    setChangePreview(null);
    onBillingFlowEnd?.();
  };

  const planName = t(`pricing.plans.${plan.id}.name`);
  const planDescription = t(`pricing.plans.${plan.id}.description`);
  const benefits = t(`pricing.plans.${plan.id}.features`, { returnObjects: true }) as string[];
  const benefitList = Array.isArray(benefits) ? benefits : [];
  const priceMultiplier = billingInterval === 'year' ? YEARLY_PRICE_MULTIPLIER : 1;
  const sharedPricingPlan = getPricingPlan(plan.id);
  const configuredPrice = sharedPricingPlan?.price ?? 0;
  const displayedPrice = configuredPrice * priceMultiplier;
  const displayedCurrency = sharedPricingPlan?.currency ?? plan.currency;
  const configuredOriginalPrice = sharedPricingPlan?.originalPrice;
  const displayedOriginalPrice = configuredOriginalPrice === undefined
    ? undefined
    : configuredOriginalPrice * priceMultiplier;
  const periodLabel = billingInterval === 'year'
    ? t('pricing.perYear')
    : t('pricing.perMonth');

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        border: '1px solid',
        borderColor: isRecommended ? 'primary.main' : 'divider',
        boxShadow: isRecommended ? 8 : 3,
        bgcolor: 'background.paper',
        transition: 'all 0.4s ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <Chip
          icon={<Sparkles sx={{ fontSize: 16 }} />}
          label={t('pricing.mostPopular')}
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 700,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiChip-icon': { color: 'inherit' },
            '& .MuiChip-label': { px: 2 }
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1, pt: isRecommended ? 4 : 3, display: 'flex', flexDirection: 'column' }}>
        {/* Plan Name */}
        <Typography variant="h5" component="h3" gutterBottom fontWeight={700}>
          {planName}
        </Typography>

        {/* Price */}
        <Box sx={{ my: 3, minHeight: 94 }}>
          {displayedOriginalPrice !== undefined && displayedOriginalPrice > displayedPrice && (
            <Typography variant="overline" color="primary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.2 }}>
              {t('pricing.priceLabels.earlyAccess')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography variant="h3" component="div" fontWeight={800} sx={{ color: configuredPrice > 0 ? 'primary.main' : 'text.primary' }}>
              {configuredPrice > 0
                ? formatMoney(displayedPrice, displayedCurrency, locale)
                : formatZeroPrice(displayedCurrency, locale)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              /{periodLabel}
            </Typography>
          </Box>
          {displayedOriginalPrice !== undefined && displayedOriginalPrice > displayedPrice && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('pricing.priceLabels.original')}
              </Typography>
              <Typography
                component="del"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '-5%',
                    right: '-5%',
                    height: '1.5px',
                    bgcolor: 'currentColor',
                    transform: 'rotate(-12deg)',
                  },
                }}
              >
                {formatMoney(displayedOriginalPrice, displayedCurrency, locale)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 40 }}>
          {planDescription}
        </Typography>

        <Divider sx={{ mb: 3, opacity: 0.5 }} />

        {/* Features list from the shared pricing locale */}
        <List dense sx={{ mb: 4, p: 0, flexGrow: 1 }}>
          {benefitList.map((benefit, i) => (
            <ListItem key={i} sx={{ px: 0, py: 0.75, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                <Check sx={{ fontSize: 18, color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={benefit}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }
                }}
              />
            </ListItem>
          ))}
        </List>

        {/* CTA Button */}
        <Button
          variant={isRecommended ? 'contained' : 'outlined'}
          fullWidth
          size="large"
          disabled={isCurrentPlan || loading || billingFlowLocked}
          onClick={handleSelectPlan}
          sx={{
            mt: 'auto',
            py: 1.5,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '1rem',
            bgcolor: isRecommended ? 'primary.main' : 'transparent',
            borderColor: isRecommended ? 'transparent' : 'divider',
            color: isRecommended ? 'primary.contrastText' : 'text.secondary',
            '&:hover': {
              bgcolor: isRecommended ? 'primary.dark' : 'action.hover',
              borderColor: isRecommended ? 'transparent' : 'primary.main',
              opacity: isRecommended ? 0.9 : 1
            },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'text.disabled',
              borderColor: 'transparent'
            }
          }}
        >
          {loading
            ? t('common.loading')
            : isCurrentPlan
              ? t('pricing.currentPlan')
              : hasExistingSubscription
                ? (displayedPrice < currentPlanPrice ? t('pricing.downgrade') : t('pricing.upgrade'))
                : plan.id === 'free'
                  ? t('pricing.actions.getStarted')
                  : (displayedPrice < currentPlanPrice ? t('pricing.downgrade') : t('pricing.upgrade'))}
        </Button>
      </CardContent>

      <Dialog
        open={changePreview !== null}
        onClose={confirming ? undefined : handleClosePlanChange}
        aria-labelledby={`plan-change-title-${plan.id}`}
        fullWidth
        maxWidth="sm"
      >
        {changePreview && (
          <>
            <DialogTitle id={`plan-change-title-${plan.id}`}>
              {t(`pricing.planChange.${changePreview.kind}Title`)}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 3 }}>
                {t('pricing.planChange.description', {
                  currentPlan: changePreview.currentPlanName,
                  targetPlan: changePreview.targetPlanName,
                })}
              </DialogContentText>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  columnGap: 3,
                  rowGap: 1.5,
                  alignItems: 'baseline',
                }}
              >
                <Typography color="text.secondary">
                  {t('pricing.planChange.currentPlan')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {changePreview.currentPlanName}
                </Typography>
                <Typography color="text.secondary">
                  {t('pricing.planChange.currentPrice')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {formatMoney(changePreview.currentPrice, changePreview.currency, locale)}/
                  {changePreview.currentBillingInterval === 'year'
                    ? t('pricing.perYear')
                    : t('pricing.perMonth')}
                </Typography>

                <Divider sx={{ gridColumn: '1 / -1', my: 0.5 }} />

                <Typography color="text.secondary">
                  {t('pricing.planChange.newPlan')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {changePreview.targetPlanName}
                </Typography>
                <Typography color="text.secondary">
                  {t('pricing.planChange.newPrice')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {formatMoney(changePreview.targetPrice, changePreview.currency, locale)}/
                  {changePreview.targetBillingInterval === 'year'
                    ? t('pricing.perYear')
                    : t('pricing.perMonth')}
                </Typography>
                <Typography color="text.secondary">
                  {t('pricing.planChange.amountDueNow')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {formatMoney(
                    changePreview.amountDueNow,
                    changePreview.currency,
                    locale
                  )}
                </Typography>
                <Typography color="text.secondary">
                  {t('pricing.planChange.effectiveDate')}
                </Typography>
                <Typography fontWeight={600} textAlign="right">
                  {formatEffectiveAt(changePreview.effectiveAt, locale)}
                </Typography>
              </Box>

              <DialogContentText sx={{ mt: 3 }}>
                {t(`pricing.planChange.${changePreview.kind}Notice`)}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={handleClosePlanChange}
                disabled={confirming}
                color="inherit"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleConfirmPlanChange}
                disabled={confirming}
                variant="contained"
                startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {confirming
                  ? t('pricing.planChange.confirming')
                  : t(`pricing.planChange.confirm${changePreview.kind[0].toUpperCase()}${changePreview.kind.slice(1)}`)}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
}
